<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline';

const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
const { isIOS, isAndroid } = useBrowserDetect();
const prompt = ref<HTMLElement | null>(null);
const appStoreUrl = 'https://apps.apple.com/ae/app/talktime-premium-therapy/id6745527693';
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=talktime.ae&pli=1';

const onKeydown = (event: KeyboardEvent) => {
   if (event.key === 'Escape') emit('close');
   if (event.key !== 'Tab') return;

   const controls = prompt.value?.querySelectorAll<HTMLElement>('button, a[href]');
   if (!controls?.length) return;
   const first = controls[0];
   const last = controls[controls.length - 1];
   if (event.shiftKey && (document.activeElement === first || document.activeElement === prompt.value)) {
      event.preventDefault();
      last.focus();
   } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
   }
};

let previousOverflow = '';
onMounted(() => {
   previousOverflow = document.body.style.overflow;
   document.body.style.overflow = 'hidden';
   prompt.value?.focus({ preventScroll: true });
   window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
   document.body.style.overflow = previousOverflow;
   window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
   <section
      ref="prompt"
      class="mobile-app-prompt fixed inset-0 z-[9999] flex flex-col overflow-x-hidden overflow-y-auto overscroll-contain bg-appPrompt-background px-5 text-gray-text"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-app-prompt-title"
      tabindex="-1"
   >
      <div class="pointer-events-none absolute inset-x-0 top-0 h-[5px] bg-gradient-to-r from-secondary-500 to-primary-500" />
      <div class="prompt-wash-teal pointer-events-none absolute rounded-full bg-appPrompt-tealWash" />
      <div class="prompt-wash-blue pointer-events-none absolute rounded-full bg-appPrompt-blueWash" />

      <header class="relative mx-auto flex w-full max-w-md shrink-0 items-center justify-between gap-4 pt-2.5">
         <img src="/image/logo.svg" alt="TalkTime" class="h-auto w-[118px]" />
         <button
            type="button"
            class="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-gray-text/5 text-gray-text hover:bg-gray-text/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            :aria-label="t('accountDetail.close')"
            @click="emit('close')"
         >
            <XMarkIcon class="h-5 w-5" aria-hidden="true" />
         </button>
      </header>

      <div class="prompt-content relative mx-auto mt-auto flex w-full max-w-[420px] flex-[0_1_auto] flex-col items-center justify-center py-2 text-center">
         <p class="text-[11px] font-extrabold uppercase tracking-[0.18em] text-primary-500">
            {{ t('mobileAppPrompt.eyebrow') }}
         </p>
         <h1 id="mobile-app-prompt-title" class="prompt-title mt-2.5 text-[clamp(25px,7.4vw,31px)] font-extrabold leading-[1.12] tracking-[-0.03em]">
            {{ t('mobileAppPrompt.titleStart') }}
            <span class="text-appPrompt-accent">{{ t('mobileAppPrompt.titleAccent') }}</span>
         </h1>
         <p class="prompt-description mt-2.5 max-w-[330px] text-[clamp(14px,3.9vw,15.5px)] font-medium leading-[1.5] text-appPrompt-subtitle">
            {{ t('mobileAppPrompt.description') }}
         </p>
         <img
            src="/image/mobile-app-collage.png"
            :alt="t('mobileAppPrompt.previewAlt')"
            class="prompt-collage mt-[18px] max-h-[34vh] w-full object-contain drop-shadow-xl"
            width="900"
            height="521"
         />
      </div>

      <footer dir="ltr" class="relative mx-auto mb-auto mt-[26px] flex w-full max-w-[200px] shrink-0 gap-[11px]">
         <a
            v-if="isIOS"
            :href="appStoreUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="min-w-0 flex-1 rounded-[9px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
         >
            <img src="/image/mobile-app-store-badge.png" :alt="t('journalAppOnly.appStoreAlt')" class="w-full rounded-[9px]" />
         </a>
         <a
            v-if="isAndroid"
            :href="googlePlayUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="min-w-0 flex-1 rounded-[9px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
         >
            <img src="/image/mobile-google-play-badge.png" :alt="t('journalAppOnly.googlePlayAlt')" class="w-full rounded-[9px]" />
         </a>
      </footer>
   </section>
</template>

<style scoped>
.mobile-app-prompt {
   padding-top: calc(env(safe-area-inset-top) + 8px);
   padding-bottom: calc(env(safe-area-inset-bottom) + 22px);
}
.prompt-content {
   min-width: 0;
   overflow-wrap: anywhere;
}
.prompt-wash-teal {
   width: 78vw;
   height: 42vh;
   inset-inline-start: -12vw;
   top: 22vh;
   opacity: 0.45;
   filter: blur(90px);
}
.prompt-wash-blue {
   width: 62vw;
   height: 38vh;
   inset-inline-end: -14vw;
   top: 38vh;
   opacity: 0.55;
   filter: blur(90px);
}
@media (max-height: 660px) {
   .prompt-title { font-size: 23px; }
   .prompt-description { display: none; }
   .prompt-collage { max-height: 28vh; }
}
</style>
